import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  FiMenu,
  FiX,
  FiChevronRight,
  FiChevronDown,
  FiLogOut,
  FiShoppingCart,
  FiUser,
} from "react-icons/fi";
import {
  HiOutlineHome,
  HiOutlineUserGroup,
  HiOutlineCube,
  HiOutlineViewGrid,
  HiOutlineShoppingCart,
  HiOutlineShoppingBag,
  HiOutlineArchive,
  HiOutlineDocumentReport,
  HiOutlineCog,
  HiOutlineClock,
  HiOutlineCreditCard,
  HiOutlineChatAlt2,
  HiOutlineCash,
} from "react-icons/hi";
import AuthService from "../../services/auth.service";

// Custom FB Catalog icon - monitor with product catalog layout
const CatalogIcon = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {/* Monitor frame */}
    <rect x="2" y="3" width="20" height="14" rx="2" />
    {/* Monitor stand */}
    <line x1="8" y1="21" x2="16" y2="21" />
    <line x1="12" y1="17" x2="12" y2="21" />
    {/* Product bag/image area (left side inside screen) */}
    <rect x="5" y="6" width="6" height="7" rx="1" />
    <path d="M7 6V5.5a1.5 1.5 0 0 1 3 0V6" />
    {/* Listing lines (right side inside screen) */}
    <line x1="14" y1="7" x2="19" y2="7" />
    <line x1="14" y1="9.5" x2="19" y2="9.5" />
    <line x1="14" y1="12" x2="17" y2="12" />
  </svg>
);

const DashboardLayout = ({ children }) => {
  const [isExpanded, setIsExpanded] = useState(false); // Desktop: collapsed by default
  const [isMobileOpen, setIsMobileOpen] = useState(false); // Mobile overlay
  const [anchorEl, setAnchorEl] = useState(null);
  const [reportsDropdownOpen, setReportsDropdownOpen] = useState(false);
  const sidebarRef = useRef(null);
  const navRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const user = AuthService.getCurrentUser();

  // Page title mapping for header subtitle
  const getPageTitle = () => {
    const pathMap = {
      "/dashboard": "Dashboard",
      "/customers": "Customers",
      "/products": "Products",
      "/categories": "Categories",
      "/orders": "Orders",
      "/retail-sales": "Retail POS",
      "/inventory": "Stock",
      "/facebook-catalog": "FB Catalog",
      "/whatsapp-chat": "WhatsApp",
      "/reports": "Due Report",
      "/payments": "Payments",
      "/advance-report": "Advance Report",
      "/settings": "Settings",
      "/pos-dashboard": "POS Dashboard",
      "/profile": "Profile",
    };
    return pathMap[location.pathname] || "Dashboard";
  };

  // Note: Removed auto-expand based on scroll detection
  // Sidebar now stays collapsed unless user clicks the arrow to expand

  // Toggle sidebar expanded state (arrow click only)
  const toggleSidebar = () => {
    const newState = !isExpanded;
    setIsExpanded(newState);
    // Close reports dropdown when collapsing
    if (!newState) {
      setReportsDropdownOpen(false);
    }
  };

  // Auto-collapse sidebar after navigation (desktop only)
  const collapseSidebar = () => {
    if (window.innerWidth >= 1024) {
      setIsExpanded(false);
      setReportsDropdownOpen(false);
    }
  };

  // Mobile toggle
  const toggleMobileSidebar = () => {
    setIsMobileOpen(!isMobileOpen);
    if (!isMobileOpen) {
      setReportsDropdownOpen(false);
    }
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    AuthService.logout();
    navigate("/login");
  };

  const handleNavigation = (path) => {
    navigate(path);
    // Close sidebar on navigation
    if (window.innerWidth < 1024) {
      setIsMobileOpen(false);
    } else {
      // Auto-collapse desktop sidebar after click
      collapseSidebar();
    }
  };

  // Reports dropdown handlers
  const handleReportsClick = () => {
    // On collapsed state, expand first then open dropdown
    if (!isExpanded && window.innerWidth >= 1024) {
      setIsExpanded(true);
    }
    setReportsDropdownOpen(!reportsDropdownOpen);
  };

  const handleReportsNavigation = (path) => {
    navigate(path);
    if (window.innerWidth < 1024) {
      setIsMobileOpen(false);
    } else {
      // Auto-collapse desktop sidebar after click
      collapseSidebar();
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (anchorEl && !event.target.closest(".user-menu-container")) {
        setAnchorEl(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [anchorEl]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        setIsMobileOpen(false);
        setAnchorEl(null);
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  const menuItems = [
    { text: "Home", icon: HiOutlineHome, path: "/dashboard" },
    { text: "Customers", icon: HiOutlineUserGroup, path: "/customers" },
    { text: "Products", icon: HiOutlineCube, path: "/products" },
    { text: "Categories", icon: HiOutlineViewGrid, path: "/categories" },
    { text: "Orders", icon: HiOutlineShoppingCart, path: "/orders" },
    { text: "Retail POS", icon: HiOutlineShoppingBag, path: "/retail-sales" },
    { text: "Stock", icon: HiOutlineArchive, path: "/inventory" },
    { text: "FB Catalog", icon: CatalogIcon, path: "/facebook-catalog" },
    { text: "WhatsApp", icon: HiOutlineChatAlt2, path: "/whatsapp-chat" },
  ];

  // Check if sidebar should show expanded state
  const showExpanded = isExpanded || isMobileOpen;

  return (
    <div className="flex min-h-screen bg-gray-50">
      {/* Top App Bar */}
      <header
        className="fixed top-0 left-0 right-0 z-50 shadow-xl"
        style={{
          background:
            "linear-gradient(135deg, #C94440 0%, #8B2E2A 60%, #7A2824 100%)",
        }}
      >
        <div className="flex items-center justify-between h-16 px-4">
          {/* Left section */}
          <div className="flex items-center gap-4">
            {/* Mobile menu button - only visible on mobile/tablet */}
            <button
              onClick={toggleMobileSidebar}
              className="lg:hidden p-2 text-white hover:bg-white/10 rounded-lg transition-all duration-200"
              aria-label="Toggle menu"
            >
              {isMobileOpen ? (
                <FiX className="w-6 h-6" />
              ) : (
                <FiMenu className="w-6 h-6" />
              )}
            </button>

            {/* Logo - Circular white background */}
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-white shadow-lg flex items-center justify-center flex-shrink-0 border-2 border-white/30">
                <img
                  src="/POS.jpg"
                  alt="POS & Inventory System"
                  className="w-10 h-10 object-contain rounded-full"
                />
              </div>
              <div className="flex flex-col">
                <h1
                  className="text-white font-bold text-lg leading-tight hidden sm:block"
                  style={{ textShadow: "0 1px 3px rgba(0,0,0,0.2)" }}
                >
                  POS & Inventory System
                </h1>
                <h1
                  className="text-white font-bold text-sm leading-tight sm:hidden"
                  style={{ textShadow: "0 1px 3px rgba(0,0,0,0.2)" }}
                >
                  Gurukrupa Dairy
                </h1>
                <span className="text-white/70 text-xs hidden sm:block">
                  {getPageTitle()}
                </span>
              </div>
            </div>
          </div>

          {/* Right section */}
          <div className="flex items-center gap-3">
            {/* POS Button - Brand themed */}
            <button
              onClick={() => handleNavigation("/pos-dashboard")}
              className="flex items-center gap-2 px-4 py-2 text-[#343232] font-semibold rounded-lg transition-all duration-200 shadow-lg text-sm hover:shadow-xl hover:scale-105"
              style={{
                background: "linear-gradient(135deg, #FFFFFF 0%, #EBA3A0 100%)",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background =
                  "linear-gradient(135deg, #F2C4C2 0%, #D68582 100%)")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background =
                  "linear-gradient(135deg, #FFFFFF 0%, #EBA3A0 100%)")
              }
            >
              <FiShoppingCart className="w-5 h-5 text-[#D64641]" />
              <span className="hidden sm:inline">POS</span>
            </button>

            {/* User Menu - Brand themed */}
            <div className="relative user-menu-container">
              <button
                onClick={handleMenuOpen}
                className="flex items-center justify-center w-10 h-10 rounded-full font-bold border-2 border-white/30 hover:scale-105 transition-all duration-200 shadow-lg text-[#343232]"
                style={{
                  background:
                    "linear-gradient(135deg, #EBA3A0 0%, #D68582 100%)",
                }}
                aria-label="User menu"
              >
                {user?.user?.name?.charAt(0)?.toUpperCase() || "A"}
              </button>

              {/* User Dropdown */}
              {anchorEl && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 overflow-hidden">
                  <div className="py-1">
                    <button
                      onClick={() => {
                        handleNavigation("/profile");
                        handleMenuClose();
                      }}
                      className="w-full text-left px-4 py-2.5 text-gray-700 hover:bg-[#EBA3A0]/20 hover:text-[#D64641] transition-colors flex items-center gap-3"
                    >
                      <FiUser className="w-4 h-4" />
                      Profile
                    </button>
                    <button
                      onClick={() => {
                        handleLogout();
                        handleMenuClose();
                      }}
                      className="w-full text-left px-4 py-2.5 text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors flex items-center gap-3"
                    >
                      <FiLogOut className="w-4 h-4" />
                      Logout
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Sidebar - Desktop (fixed, collapsible) */}
      <aside
        ref={sidebarRef}
        className={`hidden lg:flex fixed left-0 top-16 bottom-0 z-40 flex-col bg-white border-r border-gray-200 shadow-lg transition-all duration-300 ease-in-out ${
          showExpanded ? "w-64" : "w-[72px]"
        }`}
        aria-label="Main navigation"
      >
        {/* Expand/Collapse Arrow Button */}
        <button
          onClick={toggleSidebar}
          className={`absolute -right-3 top-6 z-50 flex items-center justify-center w-6 h-6 bg-white border border-gray-200 rounded-full shadow-md hover:bg-[#EBA3A0]/20 hover:border-[#D64641] transition-all duration-200 ${
            isExpanded ? "rotate-180" : ""
          }`}
          aria-label={isExpanded ? "Collapse sidebar" : "Expand sidebar"}
        >
          <FiChevronRight className="w-4 h-4 text-gray-600 hover:text-[#D64641]" />
        </button>

        {/* Custom scrollbar styles for webkit browsers */}
        <style>{`
          .sidebar-scroll::-webkit-scrollbar {
            width: 6px;
          }
          .sidebar-scroll::-webkit-scrollbar-track {
            background: transparent;
          }
          .sidebar-scroll::-webkit-scrollbar-thumb {
            background-color: #EBA3A0;
            border-radius: 3px;
          }
          .sidebar-scroll::-webkit-scrollbar-thumb:hover {
            background-color: #D64641;
          }
        `}</style>

        {/* Navigation Menu */}
        <nav
          ref={navRef}
          className="flex-1 py-1 pb-14 overflow-y-auto overflow-x-hidden sidebar-scroll"
          style={{
            scrollbarWidth: "thin",
            scrollbarColor: "#EBA3A0 transparent",
            maxHeight: "100%",
          }}
        >
          <ul className="px-1 pb-4">
            {menuItems.map((item) => {
              const isActive = location.pathname === item.path;
              const IconComponent = item.icon;
              return (
                <li key={item.text}>
                  <button
                    onClick={() => handleNavigation(item.path)}
                    className={`w-full flex items-center gap-3 px-1.5 py-2.5 rounded-xl transition-all duration-300 group relative ${
                      isActive
                        ? "text-white shadow-lg"
                        : "text-gray-700 hover:bg-[#EBA3A0]/20 hover:text-[#D64641] hover:shadow-md"
                    }`}
                    style={
                      isActive
                        ? {
                            background:
                              "linear-gradient(135deg, #D64641 0%, #B83A36 100%)",
                            boxShadow:
                              "0 10px 15px -3px rgba(214, 70, 65, 0.3)",
                          }
                        : {}
                    }
                    title={!showExpanded ? item.text : undefined}
                  >
                    {/* Active indicator */}
                    {isActive && (
                      <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-white rounded-r-full" />
                    )}
                    <div
                      className={`flex items-center justify-center w-10 h-10 rounded-xl transition-all duration-300 flex-shrink-0 ${
                        isActive
                          ? "bg-white/20 text-white"
                          : "bg-gray-100 text-gray-500 group-hover:bg-[#EBA3A0]/30 group-hover:text-[#D64641] group-hover:scale-110"
                      }`}
                    >
                      <IconComponent className="w-5 h-5" />
                    </div>
                    <span
                      className={`font-medium whitespace-nowrap transition-all duration-300 ${
                        showExpanded ? "opacity-100" : "opacity-0 w-0"
                      }`}
                    >
                      {item.text}
                    </span>
                  </button>
                </li>
              );
            })}

            {/* Reports Section with Dropdown */}
            <li>
              {(() => {
                const isReportsActive =
                  location.pathname === "/reports" ||
                  location.pathname === "/payments" ||
                  location.pathname === "/advance-report";
                return (
                  <button
                    onClick={handleReportsClick}
                    className={`w-full flex items-center justify-between px-2 py-2.5 rounded-xl transition-all duration-300 group relative ${
                      isReportsActive
                        ? "text-white shadow-lg"
                        : "text-gray-700 hover:bg-[#EBA3A0]/20 hover:text-[#D64641] hover:shadow-md"
                    }`}
                    style={
                      isReportsActive
                        ? {
                            background:
                              "linear-gradient(135deg, #D64641 0%, #B83A36 100%)",
                            boxShadow:
                              "0 10px 15px -3px rgba(214, 70, 65, 0.3)",
                          }
                        : {}
                    }
                    title={!showExpanded ? "Reports" : undefined}
                  >
                    <div className="flex items-center gap-3">
                      {isReportsActive && (
                        <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-white rounded-r-full" />
                      )}
                      <div
                        className={`flex items-center justify-center w-10 h-10 rounded-xl transition-all duration-300 flex-shrink-0 ${
                          isReportsActive
                            ? "bg-white/20 text-white"
                            : "bg-gray-100 text-gray-500 group-hover:bg-[#EBA3A0]/30 group-hover:text-[#D64641] group-hover:scale-110"
                        }`}
                      >
                        <HiOutlineDocumentReport className="w-5 h-5" />
                      </div>
                      <span
                        className={`font-medium whitespace-nowrap transition-all duration-300 ${
                          showExpanded ? "opacity-100" : "opacity-0 w-0"
                        }`}
                      >
                        Reports
                      </span>
                    </div>
                    {showExpanded && (
                      <FiChevronDown
                        className={`w-4 h-4 transition-transform duration-300 flex-shrink-0 ${
                          reportsDropdownOpen ? "rotate-180" : ""
                        }`}
                      />
                    )}
                  </button>
                );
              })()}

              {/* Reports Submenu */}
              {reportsDropdownOpen && showExpanded && (
                <ul className="mt-2 ml-4 pl-4 border-l-2 border-[#EBA3A0] space-y-1">
                  <li>
                    <button
                      onClick={() => handleReportsNavigation("/reports")}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-300 text-sm ${
                        location.pathname === "/reports"
                          ? "bg-[#EBA3A0]/30 text-[#B83A36] font-medium shadow-sm"
                          : "text-gray-600 hover:bg-[#EBA3A0]/20 hover:text-[#D64641] hover:translate-x-1"
                      }`}
                    >
                      <HiOutlineClock className="w-4 h-4" />
                      Due Report
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={() => handleReportsNavigation("/payments")}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-300 text-sm ${
                        location.pathname === "/payments"
                          ? "bg-[#EBA3A0]/30 text-[#B83A36] font-medium shadow-sm"
                          : "text-gray-600 hover:bg-[#EBA3A0]/20 hover:text-[#D64641] hover:translate-x-1"
                      }`}
                    >
                      <HiOutlineCreditCard className="w-4 h-4" />
                      Payments
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={() => handleReportsNavigation('/advance-report')}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-300 text-sm ${
                        location.pathname === '/advance-report'
                          ? 'bg-[#EBA3A0]/30 text-[#B83A36] font-medium shadow-sm'
                          : 'text-gray-600 hover:bg-[#EBA3A0]/20 hover:text-[#D64641] hover:translate-x-1'
                      }`}
                    >
                      <HiOutlineCash className="w-4 h-4" />
                      Advance Report
                    </button>
                  </li>
                </ul>
              )}
            </li>

            {/* Settings */}
            <li>
              {(() => {
                const isSettingsActive = location.pathname === "/settings";
                return (
                  <button
                    onClick={() => handleNavigation("/settings")}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-300 group relative ${
                      isSettingsActive
                        ? "text-white shadow-lg"
                        : "text-gray-700 hover:bg-[#EBA3A0]/20 hover:text-[#D64641] hover:shadow-md"
                    }`}
                    style={
                      isSettingsActive
                        ? {
                            background:
                              "linear-gradient(135deg, #D64641 0%, #B83A36 100%)",
                            boxShadow:
                              "0 10px 15px -3px rgba(214, 70, 65, 0.3)",
                          }
                        : {}
                    }
                    title={!showExpanded ? "Settings" : undefined}
                  >
                    {isSettingsActive && (
                      <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-white rounded-r-full" />
                    )}
                    <div
                      className={`flex items-center justify-center w-10 h-10 rounded-xl transition-all duration-300 flex-shrink-0 ${
                        isSettingsActive
                          ? "bg-white/20 text-white"
                          : "bg-gray-100 text-gray-500 group-hover:bg-[#EBA3A0]/30 group-hover:text-[#D64641] group-hover:scale-110"
                      }`}
                    >
                      <HiOutlineCog className="w-5 h-5" />
                    </div>
                    <span
                      className={`font-medium whitespace-nowrap transition-all duration-300 ${
                        showExpanded ? "opacity-100" : "opacity-0 w-0"
                      }`}
                    >
                      Settings
                    </span>
                  </button>
                );
              })()}
            </li>
          </ul>
        </nav>
      </aside>

      {/* Mobile Sidebar Overlay */}
      {isMobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/40 backdrop-blur-sm z-40"
          onClick={() => setIsMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Mobile Sidebar */}
      <aside
        className={`lg:hidden fixed left-0 top-16 bottom-0 w-72 z-50 bg-white shadow-2xl transform transition-transform duration-300 ease-in-out ${
          isMobileOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        aria-label="Mobile navigation"
      >
        <nav
          className="h-full py-4 pb-20 overflow-y-auto sidebar-scroll"
          style={{
            scrollbarWidth: "thin",
            scrollbarColor: "#EBA3A0 transparent",
          }}
        >
          <ul className="space-y-1 px-3 pb-4">
            {menuItems.map((item) => {
              const isActive = location.pathname === item.path;
              const IconComponent = item.icon;
              return (
                <li key={item.text}>
                  <button
                    onClick={() => handleNavigation(item.path)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 ${
                      isActive
                        ? "text-white shadow-lg"
                        : "text-gray-700 hover:bg-[#EBA3A0]/20 hover:text-[#D64641]"
                    }`}
                    style={
                      isActive
                        ? {
                            background:
                              "linear-gradient(135deg, #D64641 0%, #B83A36 100%)",
                            boxShadow:
                              "0 10px 15px -3px rgba(214, 70, 65, 0.3)",
                          }
                        : {}
                    }
                  >
                    <div
                      className={`flex items-center justify-center w-10 h-10 rounded-xl transition-all duration-300 ${
                        isActive
                          ? "bg-white/20 text-white"
                          : "bg-gray-100 text-gray-500"
                      }`}
                    >
                      <IconComponent className="w-5 h-5" />
                    </div>
                    <span className="font-medium">{item.text}</span>
                  </button>
                </li>
              );
            })}

            {/* Mobile Reports Dropdown */}
            <li>
              {(() => {
                const isReportsActive =
                  location.pathname === "/reports" ||
                  location.pathname === "/payments" ||
                  location.pathname === "/advance-report";
                return (
                  <button
                    onClick={handleReportsClick}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all duration-300 ${
                      isReportsActive
                        ? "text-white shadow-lg"
                        : "text-gray-700 hover:bg-[#EBA3A0]/20 hover:text-[#D64641]"
                    }`}
                    style={
                      isReportsActive
                        ? {
                            background:
                              "linear-gradient(135deg, #D64641 0%, #B83A36 100%)",
                            boxShadow:
                              "0 10px 15px -3px rgba(214, 70, 65, 0.3)",
                          }
                        : {}
                    }
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`flex items-center justify-center w-10 h-10 rounded-xl transition-all duration-300 ${
                          isReportsActive
                            ? "bg-white/20 text-white"
                            : "bg-gray-100 text-gray-500"
                        }`}
                      >
                        <HiOutlineDocumentReport className="w-5 h-5" />
                      </div>
                      <span className="font-medium">Reports</span>
                    </div>
                    <FiChevronDown
                      className={`w-5 h-5 transition-transform duration-300 ${
                        reportsDropdownOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                );
              })()}

              {reportsDropdownOpen && (
                <ul className="mt-2 ml-6 pl-4 border-l-2 border-[#EBA3A0] space-y-1">
                  <li>
                    <button
                      onClick={() => handleReportsNavigation("/reports")}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all duration-300 ${
                        location.pathname === "/reports"
                          ? "bg-[#EBA3A0]/30 text-[#B83A36] font-medium shadow-sm"
                          : "text-gray-600 hover:bg-[#EBA3A0]/20 hover:text-[#D64641]"
                      }`}
                    >
                      <HiOutlineClock className="w-4 h-4" />
                      Due Report
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={() => handleReportsNavigation("/payments")}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all duration-300 ${
                        location.pathname === "/payments"
                          ? "bg-[#EBA3A0]/30 text-[#B83A36] font-medium shadow-sm"
                          : "text-gray-600 hover:bg-[#EBA3A0]/20 hover:text-[#D64641]"
                      }`}
                    >
                      <HiOutlineCreditCard className="w-4 h-4" />
                      Payments
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={() => handleReportsNavigation('/advance-report')}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all duration-300 ${
                        location.pathname === '/advance-report'
                          ? 'bg-[#EBA3A0]/30 text-[#B83A36] font-medium shadow-sm'
                          : 'text-gray-600 hover:bg-[#EBA3A0]/20 hover:text-[#D64641]'
                      }`}
                    >
                      <HiOutlineCash className="w-4 h-4" />
                      Advance Report
                    </button>
                  </li>
                </ul>
              )}
            </li>

            {/* Mobile Settings */}
            <li>
              {(() => {
                const isSettingsActive = location.pathname === "/settings";
                return (
                  <button
                    onClick={() => handleNavigation("/settings")}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 ${
                      isSettingsActive
                        ? "text-white shadow-lg"
                        : "text-gray-700 hover:bg-[#EBA3A0]/20 hover:text-[#D64641]"
                    }`}
                    style={
                      isSettingsActive
                        ? {
                            background:
                              "linear-gradient(135deg, #D64641 0%, #B83A36 100%)",
                            boxShadow:
                              "0 10px 15px -3px rgba(214, 70, 65, 0.3)",
                          }
                        : {}
                    }
                  >
                    <div
                      className={`flex items-center justify-center w-10 h-10 rounded-xl transition-all duration-300 ${
                        isSettingsActive
                          ? "bg-white/20 text-white"
                          : "bg-gray-100 text-gray-500"
                      }`}
                    >
                      <HiOutlineCog className="w-5 h-5" />
                    </div>
                    <span className="font-medium">Settings</span>
                  </button>
                );
              })()}
            </li>
          </ul>
        </nav>
      </aside>

      {/* Main Content */}
      <main
        className={`flex-1 min-h-screen pt-16 transition-all duration-300 ${
          isExpanded ? "lg:ml-64" : "lg:ml-[72px]"
        }`}
      >
        <div className="p-4 md:p-6 lg:p-3 xl:p-6">{children}</div>
      </main>
    </div>
  );
};

export default DashboardLayout;
